import React, { useMemo } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, Search, HelpCircle, ChevronRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { navItemsFor, activeNavItem } from "../lib/nav";
import { modKeyLabel } from "../lib/keys";
import NotificationsBell from "./NotificationsBell";

/**
 * The bar above every page. Beside the sidebar (md and wider) it says where
 * you are (section › page), and holds search (⌘K), a new screening, the
 * bell and help. On a phone it is the navy header: the menu button, the
 * logo, search and the bell.
 */
const TopBar = ({ desktop, drawerOpen, onOpenDrawer, onOpenPalette, onOpenHelp }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { pathname } = useLocation();
  const items = useMemo(() => navItemsFor(user), [user]);
  const active = activeNavItem(pathname, items);
  const deeper = active && pathname !== active.to;

  if (!desktop) {
    return (
      <header
        className="flex items-center gap-1 h-14 pl-2 pr-1 flex-shrink-0 border-b"
        style={{ background: "#0f172a", borderColor: "#1e293b" }}
        data-testid="top-bar"
      >
        <button
          type="button"
          onClick={onOpenDrawer}
          aria-label={t("nav.openMenu")}
          aria-expanded={drawerOpen}
          aria-controls="app-sidebar"
          className="w-11 h-11 flex items-center justify-center rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 transition-colors"
          data-testid="sidebar-open"
        >
          <Menu className="w-5 h-5" />
        </button>
        <img
          src="/logos/logo-dark.png"
          alt="Audacia Corporate Services"
          className="h-8 object-contain ml-1 mr-auto min-w-0"
        />
        <button
          type="button"
          onClick={onOpenPalette}
          aria-label={t("palette.open")}
          className="w-11 h-11 flex items-center justify-center rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 transition-colors"
          data-testid="open-palette-btn"
        >
          <Search className="w-5 h-5" />
        </button>
        <NotificationsBell onDark />
      </header>
    );
  }

  return (
    <header
      className="flex items-center gap-3 h-14 px-6 flex-shrink-0 border-b bg-white dark:bg-slate-900"
      style={{ borderColor: "var(--c-border)" }}
      data-testid="top-bar"
    >
      <nav aria-label={t("nav.breadcrumb")} className="min-w-0 flex-1">
        {active && (
          <ol className="flex items-center gap-1.5 text-sm min-w-0" data-testid="breadcrumb">
            <li className="text-slate-500 dark:text-slate-400 whitespace-nowrap">{t(active.sectionLabelKey)}</li>
            <li aria-hidden="true"><ChevronRight className="w-3.5 h-3.5 text-slate-400" /></li>
            <li className="min-w-0 truncate">
              {deeper ? (
                <NavLink to={active.to} end className="text-slate-600 dark:text-slate-300 hover:text-cyan-700 dark:hover:text-cyan-400 hover:underline">
                  {t(active.labelKey)}
                </NavLink>
              ) : (
                <span className="font-semibold text-slate-900 dark:text-slate-100" aria-current="page">{t(active.labelKey)}</span>
              )}
            </li>
            {deeper && (
              <>
                <li aria-hidden="true"><ChevronRight className="w-3.5 h-3.5 text-slate-400" /></li>
                <li className="font-semibold text-slate-900 dark:text-slate-100 whitespace-nowrap" aria-current="page">{t("nav.details")}</li>
              </>
            )}
          </ol>
        )}
      </nav>

      <button
        type="button"
        onClick={onOpenPalette}
        className="flex items-center gap-2 h-9 w-56 lg:w-72 pl-3 pr-1.5 rounded-lg border text-sm text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800/60 hover:border-cyan-500 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
        style={{ borderColor: "var(--c-border)" }}
        aria-label={t("palette.open")}
        data-testid="open-palette-btn"
      >
        <Search className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
        <span className="flex-1 text-left truncate">{t("palette.searchPill")}</span>
        <kbd className="kbd" aria-hidden="true">{modKeyLabel()} K</kbd>
      </button>

      <div className="flex items-center gap-0.5">
        <NotificationsBell />
        <button
          type="button"
          onClick={onOpenHelp}
          aria-label={t("help.buttonLabel")}
          title={t("help.buttonLabel")}
          className="w-10 h-10 flex items-center justify-center rounded-lg text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          data-testid="help-center-btn"
        >
          <HelpCircle className="w-5 h-5" />
        </button>
      </div>
    </header>
  );
};

export default TopBar;
